import { Box, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import dayjs from "dayjs"
import type { FC } from "react"
import React, { useState } from "react"

import { FilterChip } from "@/components/common/Filters/FilterChip"
import { CancelDialog } from "@/components/common/Modals"
import { DATE_FORMAT } from "@/constants/formats"
import { type PriceChangeFilterInput, PriceChangeType } from "@/graphql/codegen/graphql"
import CustomerFilterChips from "@/screens/Pricing/components/filterChips/CustomerFilterChips"
import ProductCategoryFilterChip from "@/screens/Pricing/components/filterChips/ProductCategoryFilterChip"
import ProductClassFilterChip from "@/screens/Pricing/components/filterChips/ProductClassFilterChip"
import ProductManagerFilterChip from "@/screens/Pricing/components/filterChips/ProductManagerFilterChip"
import ShipFromFilterChip from "@/screens/Pricing/components/filterChips/ShipFromFilterChip"
import ShipToFilterChip from "@/screens/Pricing/components/filterChips/ShipToFilterChip"
import SpecificProductFilterChips from "@/screens/Pricing/components/filterChips/SpecificProductFilterChips"
import SupplierFilterChip from "@/screens/Pricing/components/filterChips/SupplierFilterChip"
import { PricingDialogs } from "@/screens/Pricing/constants"
import { priceChangeTypeLabelMap } from "@/screens/Pricing/constants/priceChangeLabelMap"
import { useGetPriceChangeFilterOptions } from "@/screens/Pricing/hooks/useGetPriceChangeFilterOptions"
import { usePricingDialog } from "@/screens/Pricing/hooks/usePricingDialog"
import { handleClearFilterChipData } from "@/screens/Pricing/utils/filterChip"
import { toNumberOrNull } from "@/utils/forms"

const PriceChangeFilterChips: FC = () => {
  const {
    changeType,
    customerIds = [],
    specificProductIds = [],
    supplierId,
    effectiveDate,
    productCategoryId,
    productClassId,
    productManagerId,
    shipFromId,
    shipToId,
  } = useSearch({
    from: "/app/pricing/_layout/price-change",
    strict: true,
  })

  const navigate = useNavigate({ from: "/app/pricing/price-change" })

  const { isDialogOpen, setIsDialogOpen } = usePricingDialog()
  const [pendingRemove, setPendingRemove] = useState<{
    key: keyof PriceChangeFilterInput
    value?: string | PriceChangeType
  } | null>(null)

  const { filterOptions } = useGetPriceChangeFilterOptions({
    changeType,
    supplierId,
    customerIds,
    productCategoryId,
    productManagerId,
    productClassId,
    specificProductIds: specificProductIds
      .map((specificProductId) => toNumberOrNull(specificProductId))
      .filter((v) => v !== null),
    shipToId: changeType === PriceChangeType.PurchasingCost ? shipToId : undefined,
    shipFromId,
    pause: !changeType,
  })

  const handleClearFilter = (key: keyof PriceChangeFilterInput, value?: string | PriceChangeType) => {
    setPendingRemove({ key, value })
    setIsDialogOpen(PricingDialogs.RemovePricingFilterChipConfirmation)
  }

  if (
    !changeType?.length &&
    !supplierId?.length &&
    !customerIds?.length &&
    !effectiveDate?.length &&
    !productClassId?.length &&
    !productCategoryId?.length &&
    !productManagerId?.length &&
    !specificProductIds.length &&
    !shipFromId?.length &&
    !shipToId?.length
  ) {
    return null
  }

  const confirmRemove = () => {
    if (!pendingRemove) return

    navigate({
      search: (prev: any) => {
        const newFilter = { ...prev, page: undefined }
        return handleClearFilterChipData(newFilter, pendingRemove.key, pendingRemove.value)
      },
      replace: true,
    })

    setPendingRemove(null)
    setIsDialogOpen(null)
  }

  const cancelRemove = () => {
    setPendingRemove(null)
    setIsDialogOpen(null)
  }

  return (
    <Box className='mt-6 flex flex-wrap items-center gap-2' data-testid='price-change-filter-chips'>
      <Typography variant='body1' className='text-gray-700'>
        Applied filters:
      </Typography>

      {Boolean(changeType?.length) && (
        <FilterChip
          key={`price-change-type-filter-chip`}
          label={`Change Type: ${priceChangeTypeLabelMap.get(changeType as PriceChangeType)}`}
          onDelete={() => handleClearFilter("changeType", undefined)}
        />
      )}

      {Boolean(effectiveDate?.length) && (
        <FilterChip
          key={`price-change-effective-date-filter-chip`}
          label={`Effective Date: ${dayjs(effectiveDate).format(DATE_FORMAT.US)}`}
          onDelete={() => handleClearFilter("effectiveDate", undefined)}
        />
      )}

      {Boolean(supplierId?.length) && (
        <SupplierFilterChip
          selectedSupplier={supplierId}
          suppliers={filterOptions?.suppliers}
          onRemove={handleClearFilter}
        />
      )}

      {Boolean(productCategoryId?.length) && (
        <ProductCategoryFilterChip
          selectedCategory={productCategoryId}
          productCategories={filterOptions?.productCategories}
          onRemove={handleClearFilter}
        />
      )}
      {Boolean(customerIds.length) && (
        <CustomerFilterChips
          selectedCustomerIds={customerIds}
          customers={filterOptions?.customers}
          onRemove={handleClearFilter}
        />
      )}
      {Boolean(productClassId?.length) && (
        <ProductClassFilterChip
          selectedClass={productClassId}
          productClasses={filterOptions?.productClasses}
          onRemove={handleClearFilter}
        />
      )}

      {Boolean(productManagerId?.length) && (
        <ProductManagerFilterChip
          selectedProductManagerId={productManagerId}
          productManagers={filterOptions?.productManagers}
          onRemove={handleClearFilter}
        />
      )}
      {Boolean(specificProductIds.length) && (
        <SpecificProductFilterChips
          selectedProducts={specificProductIds}
          specificProducts={filterOptions?.products}
          onRemove={handleClearFilter}
        />
      )}

      {Boolean(shipFromId?.length) && (
        <ShipFromFilterChip
          selectedShipFromWarehouse={shipFromId}
          shipFromWarehouseAreas={filterOptions?.shipFromWarehouseAreas}
          onRemove={handleClearFilter}
        />
      )}

      {Boolean(shipToId?.length) && (
        <ShipToFilterChip
          selectedShipToLocation={shipToId}
          shipToLocations={filterOptions?.shipToLocations}
          onRemove={handleClearFilter}
        />
      )}

      {isDialogOpen === PricingDialogs.RemovePricingFilterChipConfirmation && (
        <CancelDialog
          title='Altering filters will discard your changes.'
          message="You've made edits to the current results. Applying different filters will reload the content and remove those changes."
          confirmLabel='Apply Filters Anyway'
          closeLabel='Cancel'
          onClose={cancelRemove}
          onConfirm={confirmRemove}
        />
      )}
    </Box>
  )
}
export default PriceChangeFilterChips
